import { Container } from 'pixi.js';
import { Obstacle } from './Obstacle';
import { PoopObstacle } from './PoopObstacle';
import { RivalCatCameo } from './RivalCatCameo';
import { AssetLoader } from './AssetLoader';
import { ObstacleHitboxes } from './config/HitboxConfig';

export class ObstacleManager extends Container {
  private obstacles: Obstacle[] = [];
  private poopObstacles: PoopObstacle[] = [];
  private gameWidth: number;
  private groundY: number;
  private scrollSpeed = 300; // Will be updated externally
  private spawnTimer = 0;
  private baseSpawnIntervalMin = 1.6;
  private baseSpawnIntervalMax = 2.0;
  private currentSpawnInterval = 2.0;
  private isPoopSequenceActive = false;
  private isPrePoopPause = false;
  private prePoopPauseTimer = 0;
  private prePoopPauseDuration = 0.5;
  private rivalCatType: 'alici' | 'jasper' = 'jasper';
  private assetLoader: AssetLoader;
  private obstacleTypes = [
    'obstacle_boxes',
    'obstacle_cucumber',
    'obstacle_flowerpot',
    'obstacle_laundry',
    'obstacle_roomba',
    'obstacle_waterpool',
    'obstacle_washingmachine',
  ];

  private poopCooldownTimer = 0;
  private poopCooldownMin = 6;
  private poopCooldownMax = 10;
  private nextPoopCooldown = 0;
  private currentCameo: RivalCatCameo | null = null;

  constructor(gameWidth: number, groundY: number) {
    super();
    this.gameWidth = gameWidth;
    this.groundY = groundY;
    this.assetLoader = AssetLoader.getInstance();
    this.nextPoopCooldown = this.getRandomPoopCooldown();
    this.currentSpawnInterval = this.getRandomSpawnInterval();
  }

  private getRandomPoopCooldown(): number {
    return this.poopCooldownMin + Math.random() * (this.poopCooldownMax - this.poopCooldownMin);
  }

  private getRandomSpawnInterval(): number {
    // Adjust spawn interval based on speed
    // As speed increases from 300 to 600, spawn rate increases by up to 30%
    const speedFactor = Math.min((this.scrollSpeed - 300) / 300, 1); // 0 to 1
    const speedMultiplier = 1 - speedFactor * 0.3; // 1.0 to 0.7

    const adjustedMin = this.baseSpawnIntervalMin * speedMultiplier;
    const adjustedMax = this.baseSpawnIntervalMax * speedMultiplier;

    return adjustedMin + Math.random() * (adjustedMax - adjustedMin);
  }

  update(deltaTime: number): void {
    this.spawnTimer += deltaTime;

    if (this.spawnTimer >= this.currentSpawnInterval && !this.isPoopSequenceActive && !this.isPrePoopPause) {
      this.spawnObstacle();
      this.spawnTimer = 0;
      this.currentSpawnInterval = this.getRandomSpawnInterval();
    }

    this.poopCooldownTimer += deltaTime;
    if (this.poopCooldownTimer >= this.nextPoopCooldown && !this.currentCameo && !this.isPrePoopPause) {
      this.startPrePoopPause();
      this.poopCooldownTimer = 0;
      this.nextPoopCooldown = this.getRandomPoopCooldown();
    }

    if (this.isPrePoopPause) {
      this.prePoopPauseTimer += deltaTime;
      if (this.prePoopPauseTimer >= this.prePoopPauseDuration) {
        this.isPrePoopPause = false;
        this.prePoopPauseTimer = 0;
        this.spawnPoopSequence();
      }
    }

    if (this.currentCameo) {
      this.currentCameo.update(deltaTime);
    }

    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      const obstacle = this.obstacles[i];
      obstacle.update(this.scrollSpeed, deltaTime);

      if (obstacle.isOffScreen()) {
        this.removeChild(obstacle);
        this.obstacles.splice(i, 1);
      }
    }

    for (let i = this.poopObstacles.length - 1; i >= 0; i--) {
      const poopObstacle = this.poopObstacles[i];
      poopObstacle.update(this.scrollSpeed, deltaTime);

      if (poopObstacle.isOffScreen()) {
        this.removeChild(poopObstacle);
        this.poopObstacles.splice(i, 1);
      }
    }
  }

  private spawnObstacle(): void {
    const randomIndex = Math.floor(Math.random() * this.obstacleTypes.length);
    const obstacleType = this.obstacleTypes[randomIndex];
    const texture = this.assetLoader.getTexture(obstacleType);

    const x = this.gameWidth + 50;
    const config = ObstacleHitboxes[obstacleType];
    const scale = config?.scale || 0.5;

    const obstacle = new Obstacle(x, this.groundY, texture, obstacleType, scale);
    this.obstacles.push(obstacle);
    this.addChild(obstacle);
  }

  private startPrePoopPause(): void {
    this.isPrePoopPause = true;
    this.prePoopPauseTimer = 0;
  }

  private spawnPoopSequence(): void {
    this.isPoopSequenceActive = true;
    this.currentCameo = new RivalCatCameo(this.gameWidth, this.groundY, this.rivalCatType);
    this.addChild(this.currentCameo);

    // Spawn poop during squat phase
    this.currentCameo.onPoopSpawn(() => {
      if (this.currentCameo) {
        const spawnX = this.currentCameo.getPoopSpawnX();
        const poopObstacle = new PoopObstacle(spawnX, this.groundY);
        this.poopObstacles.push(poopObstacle);
        this.addChild(poopObstacle);
      }
    });

    // Remove cameo when walk-out animation is complete
    this.currentCameo.onTelegraphComplete(() => {
      if (this.currentCameo) {
        this.removeChild(this.currentCameo);
        this.currentCameo = null;
        this.isPoopSequenceActive = false;
      }
    });
  }

  checkCollision(
    playerHitbox: { x: number; y: number; width: number; height: number },
    isPlayerStunned: boolean
  ): { type: 'none' | 'solid' | 'poop' } {
    for (const obstacle of this.obstacles) {
      const obstacleHitbox = obstacle.getHitbox();

      if (
        playerHitbox.x < obstacleHitbox.x + obstacleHitbox.width &&
        playerHitbox.x + playerHitbox.width > obstacleHitbox.x &&
        playerHitbox.y < obstacleHitbox.y + obstacleHitbox.height &&
        playerHitbox.y + playerHitbox.height > obstacleHitbox.y
      ) {
        return { type: 'solid' };
      }
    }

    if (!isPlayerStunned) {
      for (const poopObstacle of this.poopObstacles) {
        if (poopObstacle.getHasBeenHit()) continue;

        const poopHitbox = poopObstacle.getHitbox();

        if (
          playerHitbox.x < poopHitbox.x + poopHitbox.width &&
          playerHitbox.x + playerHitbox.width > poopHitbox.x &&
          playerHitbox.y < poopHitbox.y + poopHitbox.height &&
          playerHitbox.y + playerHitbox.height > poopHitbox.y
        ) {
          poopObstacle.markAsHit();
          return { type: 'poop' };
        }
      }
    }

    return { type: 'none' };
  }

  getScrollSpeed(): number {
    return this.scrollSpeed;
  }

  setScrollSpeed(speed: number): void {
    this.scrollSpeed = speed;
    // Recalculate spawn interval when speed changes
    // This ensures the interval adapts to the current difficulty
  }

  updateDimensions(gameWidth: number, groundY: number): void {
    this.gameWidth = gameWidth;
    this.groundY = groundY;
  }

  reset(): void {
    for (const obstacle of this.obstacles) {
      this.removeChild(obstacle);
    }
    this.obstacles = [];

    for (const poopObstacle of this.poopObstacles) {
      this.removeChild(poopObstacle);
    }
    this.poopObstacles = [];

    if (this.currentCameo) {
      this.removeChild(this.currentCameo);
      this.currentCameo = null;
    }

    this.spawnTimer = 0;
    this.poopCooldownTimer = 0;
    this.nextPoopCooldown = this.getRandomPoopCooldown();
    this.currentSpawnInterval = this.getRandomSpawnInterval();
    this.isPoopSequenceActive = false;
    this.isPrePoopPause = false;
    this.prePoopPauseTimer = 0;
  }

  setRivalCat(catType: 'alici' | 'jasper'): void {
    this.rivalCatType = catType;
  }

  getCurrentSpawnInterval(): number {
    return this.currentSpawnInterval;
  }
}
